import { useEffect, useState, useMemo, useCallback } from 'react'
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet'
import { LatLngExpression } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Loader2, AlertCircle } from 'lucide-react'

// US State coordinates for zipcode mapping (approximate centers)
const US_STATE_COORDS: Record<string, [number, number]> = {
  AL: [32.806671, -86.791130], AK: [61.370716, -152.404419], AZ: [33.729759, -111.431221],
  AR: [34.969704, -92.373123], CA: [36.116203, -119.681564], CO: [39.059811, -105.311104],
  CT: [41.597782, -72.755371], DE: [39.318523, -75.507141], FL: [27.766279, -81.686783],
  GA: [33.040619, -83.643074], HI: [21.094318, -157.498337], ID: [44.240459, -114.478828],
  IL: [40.349457, -88.986137], IN: [39.849426, -86.258278], IA: [42.011539, -93.210526],
  KS: [38.526600, -96.726486], KY: [37.668140, -84.670067], LA: [31.169546, -91.867805],
  ME: [44.693947, -69.381927], MD: [39.063946, -76.802101], MA: [42.230171, -71.530106],
  MI: [43.326618, -84.536095], MN: [45.694454, -93.900192], MS: [32.741646, -89.678696],
  MO: [38.456085, -92.288368], MT: [46.921925, -110.454353], NE: [41.125370, -98.268082],
  NV: [38.313515, -117.055374], NH: [43.452492, -71.563896], NJ: [40.298904, -74.521011],
  NM: [34.840515, -106.248482], NY: [42.165726, -74.948051], NC: [35.630066, -79.806419],
  ND: [47.528912, -99.784012], OH: [40.388783, -82.764915], OK: [35.565342, -96.928917],
  OR: [44.572021, -122.070938], PA: [40.590752, -77.209755], RI: [41.680893, -71.511780],
  SC: [33.856892, -80.945007], SD: [44.299782, -99.438828], TN: [35.747845, -86.692345],
  TX: [31.054487, -97.563461], UT: [40.150032, -111.862434], VT: [44.045876, -72.710686],
  VA: [37.769337, -78.169968], WA: [47.400902, -121.490494], WV: [38.491226, -80.954453],
  WI: [44.268543, -89.616508], WY: [42.755966, -107.302490]
}

// Zipcode to approximate coordinates (first 3 digits)
const ZIPCODE_PREFIX_COORDS: Record<string, [number, number]> = {
  '100': [40.7128, -74.0060], '101': [40.7128, -74.0060], '102': [40.7489, -73.9680], // NY
  '190': [39.9526, -75.1652], '191': [39.9526, -75.1652], '193': [39.9526, -75.1652], // PA
  '430': [40.4173, -82.9071], '431': [40.4173, -82.9071], '432': [39.9612, -82.9988], // OH
  '570': [43.6532, -70.2594], '571': [43.6532, -70.2594], // SD
  '105': [42.9634, -73.7454], // NY (Hoosick Falls area)
}

interface PFASMapProps {
  eligibleZipcodes?: Array<string | {
    zipcode: string
    state?: string
    city?: string
    contaminationLevel?: string
  }>
  highlightedZipcode?: string
  onZipcodeClick?: (zipcode: string) => void
  loading?: boolean
}

// Component to handle map view updates
function MapViewController({ center, zoom }: { center: LatLngExpression; zoom: number }) {
  const map = useMap()

  useEffect(() => {
    map.setView(center, zoom)
  }, [center, zoom, map])

  return null
}


export default function PFASMap({
  eligibleZipcodes = [],
  highlightedZipcode,
  onZipcodeClick,
  loading = false
}: PFASMapProps) {
  const [mapCenter, setMapCenter] = useState<LatLngExpression>([39.8283, -98.5795]) // US center
  const [mapZoom, setMapZoom] = useState(4)

  // Removed verbose debug logging for performance

  // Cache for geocoded coordinates (currently unused - geocoding disabled for performance)
  const [geocodeCache] = useState<Record<string, [number, number]>>({})

  // Get coordinates for a zipcode with geocoding fallback
  const getZipcodeCoordinates = useCallback((zipcode: string, state?: string): [number, number] | null => {
    if (!zipcode || zipcode.length < 3) return null

    // Check geocode cache first
    if (geocodeCache[zipcode]) {
      return geocodeCache[zipcode]
    }

    // Try state first if available - use deterministic spread based on zipcode
    if (state && US_STATE_COORDS[state]) {
      const [lat, lng] = US_STATE_COORDS[state]
      // Use zipcode digits for deterministic offset (not random - faster and consistent)
      const hash = parseInt(zipcode.slice(-3), 10) / 1000 // 0-0.999
      const latOffset = (hash - 0.5) * 1.0
      const lngOffset = ((1 - hash) - 0.5) * 1.0
      return [lat + latOffset, lng + lngOffset]
    }

    // Try zipcode prefix lookup
    const prefix = zipcode.substring(0, 3)
    if (ZIPCODE_PREFIX_COORDS[prefix]) {
      const [lat, lng] = ZIPCODE_PREFIX_COORDS[prefix]
      const hash = parseInt(zipcode.slice(-2), 10) / 100
      const latOffset = (hash - 0.5) * 0.5
      const lngOffset = ((1 - hash) - 0.5) * 0.5
      return [lat + latOffset, lng + lngOffset]
    }

    // Fallback: estimate based on first digit with better granularity
    const firstDigit = zipcode[0]
    const secondDigit = zipcode[1] || '0'

    // Base regional coordinates by first digit
    const regionCenters: Record<string, [number, number]> = {
      '0': [42.3601, -71.0589], // Northeast (MA, CT, ME, NH, VT, RI)
      '1': [40.7128, -74.0060], // NY/NJ/PA
      '2': [38.9072, -77.0369], // DC/MD/VA/WV
      '3': [33.7490, -84.3880], // Southeast (AL, FL, GA, MS, NC, SC, TN)
      '4': [38.2527, -85.7585], // KY/IN/OH/MI
      '5': [41.8781, -87.6298], // Midwest (IL, IA, MN, WI, ND, SD, MT)
      '6': [35.4676, -97.5164], // South Central (TX, OK, AR, LA, NM, KS, MO)
      '7': [39.0997, -94.5786], // Central (NE, CO, WY)
      '8': [39.7392, -104.9903], // Mountain (CO, AZ, UT, NV, ID, WY)
      '9': [37.7749, -122.4194], // West Coast (CA, OR, WA, AK, HI)
    }

    const baseCoords = regionCenters[firstDigit] || [39.8283, -98.5795]

    // Use deterministic offset based on zipcode digits (not random)
    const secondDigitOffset = parseInt(secondDigit, 10) / 10
    const hash = parseInt(zipcode.slice(-2), 10) / 100
    const latOffset = (hash + secondDigitOffset - 0.5) * 2.0
    const lngOffset = ((1 - hash) + secondDigitOffset - 0.5) * 2.0

    return [baseCoords[0] + latOffset, baseCoords[1] + lngOffset]
  }, [geocodeCache])


  // Process zipcodes for map markers - optimized for performance
  const mapMarkers = useMemo(() => {
    if (eligibleZipcodes.length === 0) return []

    // Normalize data - convert strings to objects
    const normalized = eligibleZipcodes
      .filter(item => item) // Remove null/undefined
      .map(item => {
        // If it's a string, convert to object format
        if (typeof item === 'string') {
          return {
            zipcode: item,
            state: undefined,
            city: undefined,
            contaminationLevel: undefined
          }
        }
        // Already an object
        return item
      })

    // Sample data for better performance (show every 10th zipcode for large datasets)
    const sampleRate = normalized.length > 10000 ? 10 : normalized.length > 5000 ? 5 : 1
    const sampledData = sampleRate > 1
      ? normalized.filter((_, index) => index % sampleRate === 0)
      : normalized

    // Process markers with optimized loop (avoid nested maps)
    const markers = []
    for (let i = 0; i < sampledData.length; i++) {
      const item = sampledData[i]

      // Handle zipcode ranges like "57049-57198"
      const zip = item.zipcode.includes('-')
        ? item.zipcode.split('-')[0] // Use first zipcode in range
        : item.zipcode

      const coords = getZipcodeCoordinates(zip, item.state)

      if (coords) {
        markers.push({
          zipcode: zip,
          originalZipcode: item.zipcode,
          coordinates: coords,
          state: item.state,
          city: item.city,
          contaminationLevel: item.contaminationLevel,
          isHighlighted: zip === highlightedZipcode || item.zipcode === highlightedZipcode
        })
      }
    }

    // Always include highlighted zipcode if present
    if (highlightedZipcode && !markers.some(m => m.isHighlighted)) {
      const highlightedItem = normalized.find(item =>
        item.zipcode === highlightedZipcode ||
        (item.zipcode.includes('-') && item.zipcode.split('-')[0] === highlightedZipcode)
      )

      if (highlightedItem) {
        const zip = highlightedItem.zipcode.includes('-')
          ? highlightedItem.zipcode.split('-')[0]
          : highlightedItem.zipcode
        const coords = getZipcodeCoordinates(zip, highlightedItem.state)

        if (coords) {
          markers.push({
            zipcode: zip,
            originalZipcode: highlightedItem.zipcode,
            coordinates: coords,
            state: highlightedItem.state,
            city: highlightedItem.city,
            contaminationLevel: highlightedItem.contaminationLevel,
            isHighlighted: true
          })
        }
      }
    }

    return markers
  }, [eligibleZipcodes, highlightedZipcode, getZipcodeCoordinates])

  // Update map view when highlighted zipcode changes
  useEffect(() => {
    if (highlightedZipcode) {
      const marker = mapMarkers.find(m =>
        m.zipcode === highlightedZipcode ||
        m.originalZipcode === highlightedZipcode
      )
      if (marker) {
        setMapCenter(marker.coordinates)
        setMapZoom(10)
      }
    } else {
      // Reset to US view
      setMapCenter([39.8283, -98.5795])
      setMapZoom(4)
    }
  }, [highlightedZipcode, mapMarkers])

  // Get color based on contamination level
  const getContaminationColor = (level?: string): string => {
    if (!level) return '#3B82F6' // Default blue

    if (level.includes('71+') || level.toLowerCase().includes('high')) {
      return '#DC2626' // Red for high contamination (71+ PPT)
    } else if (level.includes('41') || level.toLowerCase().includes('elevated')) {
      return '#F59E0B' // Orange for elevated contamination (41-70.99 PPT)
    }

    return '#3B82F6' // Blue for other cases
  }

  if (loading) {
    return (
      <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Loading PFAS Contamination Map...</p>
        </div>
      </div>
    )
  }

  if (mapMarkers.length === 0) {
    return (
      <div className="bg-gray-100 rounded-lg h-96 flex items-center justify-center border-2 border-dashed border-gray-300">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No PFAS contamination data available</p>
          <p className="text-sm text-gray-500 mt-2">
            Unable to load eligible zipcodes. Please contact support.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Map Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-700">
          <span className="font-semibold">Showing {mapMarkers.length} contaminated zipcodes</span> across the United States with documented PFAS contamination.
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Click on any marker to auto-fill the zipcode above, or zoom and pan to explore.
        </p>
      </div>

      {/* Map Container */}
      <div className="rounded-lg overflow-hidden shadow-lg border-2 border-gray-300" style={{ height: '500px' }}>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <MapViewController center={mapCenter} zoom={mapZoom} />

          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {mapMarkers.map((marker, index) => {
            const color = getContaminationColor(marker.contaminationLevel)
            const radius = marker.isHighlighted ? 10 : 5

            return (
              <CircleMarker
                key={`${marker.zipcode}-${index}`}
                center={marker.coordinates}
                radius={radius}
                pathOptions={{
                  fillColor: color,
                  fillOpacity: 1,
                  color: 'transparent',
                  weight: 0,
                  stroke: false
                }}
                eventHandlers={{
                  click: () => {
                    if (onZipcodeClick) {
                      onZipcodeClick(marker.originalZipcode)
                    }
                  }
                }}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-bold text-gray-900">
                      Zipcode: {marker.originalZipcode}
                    </p>
                    {marker.state && <p className="text-gray-700">State: {marker.state}</p>}
                  </div>
                </Popup>
              </CircleMarker>
            )
          })}
        </MapContainer>
      </div>

      {/* Map Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          <strong>How to use:</strong> Zoom and pan to explore contaminated areas.
          Click on markers to see details. Enter your zipcode above to check eligibility.
        </p>
      </div>
    </div>
  )
}
